import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  DEFAULT_LANGUAGE, LANGUAGE_NAMES, SUPPORTED_LENGTHS, buildChallengeUrl, isSupportedLength,
  loadLength,
} from '@lexicon/core';
import { Toast } from '../components/Toast';
import type { ToastMessage } from '../game/useGame';
import { createChallenge } from '../lib/api';
import { addMyChallenge } from '../lib/myChallenges';
import { shareOrigin } from '../lib/platform';
import { navigate } from '../lib/router';
import { shareResult } from '../lib/share';
import { useApp, useStrings } from '../state/AppContext';

const TOAST_MS = 2000;

const FIELD_CLASS = 'min-h-[52px] w-full rounded-xl bg-black/25 px-3 text-base';
const LABEL_CLASS = 'text-xs tracking-[0.14em] text-dim';

const MIN_WORD_LENGTH = Math.min(...SUPPORTED_LENGTHS);
const MAX_WORD_LENGTH = Math.max(...SUPPORTED_LENGTHS);

interface ReadyCard {
  code: string;
  word: string;
}

function LinkButtons({ onShare, onCopy }: {
  onShare: () => void;
  onCopy: () => void;
}) {
  const t = useStrings();
  return (
    <div className="mt-3 flex flex-col gap-2">
      <button
        type="button"
        onClick={onShare}
        className="min-h-[52px] rounded-xl bg-grad-accent text-sm font-semibold text-white"
      >
        {t.newChallenge.shareLink}
      </button>
      <button
        type="button"
        onClick={onCopy}
        className="min-h-[52px] rounded-xl border border-hairline text-sm font-semibold"
      >
        {t.newChallenge.copyLink}
      </button>
    </div>
  );
}

export function NewChallengeScreen() {
  const { storage, deviceId, savePlayerName, language, t } = useApp();

  const [word, setWord] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<ReadyCard | null>(null);
  const [offlineWord, setOfflineWord] = useState<string | null>(null);

  const [toast, setToast] = useState<ToastMessage | null>(null);
  const toastSeq = useRef(0);

  // Captured once at mount rather than read fresh on every click: browser
  // clipboards are stable for a page's lifetime, but some tooling (e.g.
  // testing-library/user-event installs its own clipboard stub the first
  // time a session starts) swaps `navigator.clipboard` out from under a
  // long-lived screen. Holding the object we saw when the screen opened
  // keeps Share/Copy pointed at one target instead of whatever is current.
  const clipboardRef = useRef(typeof navigator === 'undefined' ? undefined : navigator.clipboard);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const showToast = useCallback((text: string) => {
    toastSeq.current += 1;
    setToast({ id: toastSeq.current, text });
  }, []);

  const handleWordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setWord(e.target.value.toLowerCase().replace(/[^a-z]/g, '').slice(0, MAX_WORD_LENGTH));
  }, []);

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value.slice(0, 20));
  }, []);

  const handleShare = useCallback((url: string) => {
    void (async () => {
      // `.share` is bound to the real navigator (native share needs the
      // correct `this`) and read fresh — only `.clipboard` is swapped by
      // tooling, so only it needs the captured reference.
      const nav = {
        share: typeof navigator.share === 'function' ? navigator.share.bind(navigator) : undefined,
        clipboard: clipboardRef.current,
      } as Navigator;
      const outcome = await shareResult(t.challenge.invite(url), nav);
      if (outcome === 'copied') showToast(t.common.copied);
      if (outcome === 'failed') showToast(t.common.shareFailed);
    })();
  }, [showToast, t]);

  const handleCopy = useCallback((url: string) => {
    void (async () => {
      try {
        if (!clipboardRef.current) throw new Error('Clipboard unavailable');
        await clipboardRef.current.writeText(url);
        showToast(t.common.copied);
      } catch {
        showToast(t.common.shareFailed);
      }
    })();
  }, [showToast, t]);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setCard(null);
    setOfflineWord(null);

    if (!isSupportedLength(word.length)) {
      setError(t.newChallenge.lengthError(MIN_WORD_LENGTH, MAX_WORD_LENGTH));
      return;
    }
    setError(null);
    setSubmitting(true);
    const trimmedName = name.trim();
    void (async () => {
      // The accepted-guess list for a non-5 length is its own chunk
      // (spec §10.5), so "is this a word?" is now an await.
      // Checked against the language being played, not against whichever
      // language happens to accept the word: a Dutch player writing
      // `regen` means the Dutch word, and their friend should get the
      // Dutch dictionary for it (the worker is told, and stores it).
      let isValid: (w: string) => boolean;
      try {
        ({ isValid } = await loadLength(word.length, language));
      } catch {
        setSubmitting(false);
        setError(t.newChallenge.listError);
        return;
      }
      if (!isValid(word)) {
        setSubmitting(false);
        setError(t.newChallenge.notInList);
        return;
      }

      const res = await createChallenge({
        word, challengerName: trimmedName || undefined, deviceId, lang: language,
      });
      setSubmitting(false);
      if (res.ok) {
        const code = res.value;
        if (trimmedName) await savePlayerName(trimmedName);
        await addMyChallenge(storage, {
          code, word, createdAt: Date.now(), challengerName: trimmedName || undefined,
        });
        setCard({ code, word });
        // M8: leaving the just-created word in the field let a second,
        // unchanged submit mint a duplicate challenge for the same word.
        setWord('');
        return;
      }
      if (res.error === 'rate-limited') {
        setError(t.newChallenge.rateLimited);
        return;
      }
      setError(t.newChallenge.serverError);
      setOfflineWord(word);
    })();
  }, [word, name, submitting, deviceId, storage, savePlayerName, language, t]);

  // The language rides in the fragment too, so a link that never reaches
  // the server still tells the recipient which word list to load.
  const readyUrl = card ? buildChallengeUrl(shareOrigin(), card.code, card.word, language) : null;
  const offlineUrl = offlineWord
    ? buildChallengeUrl(shareOrigin(), 'offline', offlineWord, language)
    : null;

  return (
    <section className="flex flex-1 flex-col gap-4">
      {/* I3: /challenge/new is a dead end without this — there is no
          browser chrome to fall back on inside the Capacitor shell. */}
      <button
        type="button"
        className="glass min-h-[44px] self-start rounded-xl px-4 text-sm font-semibold"
        onClick={() => navigate('/')}
      >
        {t.common.toToday}
      </button>

      <h2 className="text-lg font-semibold">{t.newChallenge.title}</h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="challenge-word" className={LABEL_CLASS}>{t.newChallenge.wordLabel}</label>
          <input
            id="challenge-word"
            maxLength={MAX_WORD_LENGTH}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t.newChallenge.wordPlaceholder(MIN_WORD_LENGTH, MAX_WORD_LENGTH)}
            value={word}
            onChange={handleWordChange}
            // F3 review fix: the word list load + dictionary check now
            // spans an await, so without this the field stayed editable
            // through a pending submit — an edit mid-flight raced the
            // in-flight `word`, and a since-cleared error could go stale.
            disabled={submitting}
            className={FIELD_CLASS}
          />
          {/* Which list the word is checked against is not obvious from a
              field that takes any letters, and it decides whether the
              friend's board accepts their guesses. Shown only when it is
              not the app's original language. */}
          {language !== DEFAULT_LANGUAGE ? (
            <p className="text-xs text-dim">
              {t.newChallenge.wordLanguageNote(LANGUAGE_NAMES[language])}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="challenger-name" className={LABEL_CLASS}>{t.newChallenge.nameLabel}</label>
          <input
            id="challenger-name"
            maxLength={20}
            placeholder={t.newChallenge.namePlaceholder}
            value={name}
            onChange={handleNameChange}
            className={FIELD_CLASS}
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="min-h-[52px] rounded-xl bg-grad-accent text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? t.newChallenge.creating : t.newChallenge.create}
        </button>
      </form>

      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}

      {card && readyUrl ? (
        <>
          <section aria-label={t.newChallenge.readyLabel} className="glass rounded-2xl p-4">
            <h3 className="text-base font-semibold">{t.newChallenge.ready}</h3>
            <p className="break-all text-xs text-dim">{readyUrl}</p>
            <LinkButtons
              onShare={() => handleShare(readyUrl)}
              onCopy={() => handleCopy(readyUrl)}
            />
          </section>
          <button
            type="button"
            className="min-h-[44px] rounded-xl border border-hairline text-sm font-semibold"
            onClick={() => navigate('/')}
          >
            {t.common.backToToday}
          </button>
        </>
      ) : null}

      {offlineUrl ? (
        <section aria-label={t.newChallenge.offlineLabel} className="glass rounded-2xl p-4">
          <h3 className="text-base font-semibold">{t.newChallenge.offlineTitle}</h3>
          <p className="text-sm text-dim">{t.newChallenge.offlineBody}</p>
          <p className="break-all text-xs text-dim">{offlineUrl}</p>
          <LinkButtons
            onShare={() => handleShare(offlineUrl)}
            onCopy={() => handleCopy(offlineUrl)}
          />
        </section>
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}
